import {
  CreateParams,
  CreateResult,
  DeleteParams,
  DeleteResult,
  GetListParams,
  GetListResult,
  GetManyReferenceParams,
  GetManyReferenceResult,
  GetOneParams,
  GetOneResult,
  UpdateParams,
  UpdateResult,
} from "react-admin";
import isEqual from "lodash/isEqual";
import { v4 as uuidv4 } from "uuid";
import { applyFilter, applyPagination, applySort } from "../backoffice.utils";
import { AbstractDataProvider } from "./AbstractDataProvider";
import { type ExamLocation, ExamLocationSchema } from "../model/ExamLocation";
import { CreateExamLocationDto, ExamLocationDto, schemas, serverAPI, UpdateExamLocationDto } from "../api/server.api";

class ExamLocationsProvider extends AbstractDataProvider<ExamLocation> {
  async getOne(_resource: string, { id, meta }: GetOneParams<ExamLocation>): Promise<GetOneResult<ExamLocation>> {
    if (!meta.drivingSchoolId) {
      throw new Error(`drivingSchoolId is required in meta: ${JSON.stringify({ id })}`);
    }
    const dto = await serverAPI.getExamLocation({ id, drivingSchoolId: meta.drivingSchoolId });
    return { data: _fromDto(dto, meta.drivingSchoolId) };
  }

  async getList(_resource: string, { filter, sort, pagination }: GetListParams): Promise<GetListResult<ExamLocation>> {
    const { drivingSchoolId, ...clientSideFilter } = filter;
    if (!drivingSchoolId) {
      throw new Error(`Unexpected filter: ${JSON.stringify(filter)} -- expected: {"drivingSchoolId": ...}`);
    }
    const dtos = await serverAPI.getExamLocations({ drivingSchoolId });
    const records = dtos.map((dto) => _fromDto(dto, drivingSchoolId));
    return applyPagination(applySort(applyFilter(records, clientSideFilter), sort), pagination);
  }

  async getManyReference(
    _resource: string,
    { target, id, filter, sort, pagination }: GetManyReferenceParams,
  ): Promise<GetManyReferenceResult<ExamLocation>> {
    if (target !== "drivingSchoolId") {
      throw new Error(`Invalid target: ${JSON.stringify(target)} -- expected: "drivingSchoolId"`);
    }
    return this.getList("examLocations", { filter: { drivingSchoolId: id, ...filter }, sort, pagination });
  }

  async create(_resource: string, { data, meta }: CreateParams<ExamLocation>): Promise<CreateResult<ExamLocation>> {
    const drivingSchoolId = meta?.drivingSchoolId;
    if (!drivingSchoolId) {
      throw new Error(`Invalid meta: ${JSON.stringify(meta)} -- expected: {drivingSchoolId: ...}`);
    }
    const dto = _toCreateDto(data);
    await serverAPI.createExamLocation({ dto, drivingSchoolId });
    const result = await this.getOne("examLocations", { id: dto.uid, meta: { drivingSchoolId } });
    return { data: result.data };
  }

  async update(
    _resource: string,
    { id, data, previousData, meta }: UpdateParams<ExamLocation & { drivingSchoolId: string }>,
  ): Promise<UpdateResult<ExamLocation>> {
    const drivingSchoolId = meta?.drivingSchoolId;
    if (!drivingSchoolId) {
      throw new Error(`Invalid meta: ${JSON.stringify(meta)} -- expected: {drivingSchoolId: ...}`);
    }
    const dto = _toUpdateDto(data, previousData);
    await serverAPI.updateExamLocation({
      id,
      dto,
      drivingSchoolId: drivingSchoolId,
    });
    const result = await this.getOne("examLocations", { id, meta: { drivingSchoolId } });
    return { data: result.data };
  }

  async delete(_: string, params: DeleteParams<ExamLocation>): Promise<DeleteResult> {
    const drivingSchoolId = params.meta?.drivingSchoolId;
    if (!drivingSchoolId) {
      throw new Error(`Invalid meta: ${JSON.stringify(params.meta)} -- expected: {drivingSchoolId: ...}`);
    }
    await serverAPI.deleteExamLocation({ id: params.id, drivingSchoolId });
    return { data: {} };
  }
}

export const examLocationsProvider = new ExamLocationsProvider();
export { ExamLocation };

function _fromDto(dto: ExamLocationDto, drivingSchoolId: string): ExamLocation {
  try {
    const { uid, ...rest } = ExamLocationSchema.parse(dto);
    return {
      id: uid,
      drivingSchoolId,
      ...rest,
    };
  } catch (error) {
    console.error("Error parsing ExamLocationDto", error);
    throw error;
  }
}

function _toCreateDto(data: Partial<ExamLocation>): CreateExamLocationDto {
  return schemas.CreateExamLocationDto.parse({ ...data, uid: uuidv4() });
}

function _toUpdateDto(data: Partial<ExamLocation>, previousData: ExamLocation): UpdateExamLocationDto {
  const delta = Object.fromEntries(
    Object.entries(data).filter(([k, v]) => !isEqual(v, previousData[k as keyof ExamLocation])),
  );
  // "types" should always be sent as it's a dependency for other fields on the server
  return schemas.UpdateExamLocationDto.parse({ ...delta, types: data.types });
}
